/**
 * Device display labels — pure string formatters for UI presentation.
 *
 * Single source of truth for "what do we call this device?" across
 *   - apps/web MDM tables, drawers, cards (features/mdm-devices)
 *   - apps/staff customer device tables
 *   - any future surface that renders a device
 *
 * Design principles:
 *   - Takes a normalized `DeviceClassification` as input, never raw device fields.
 *     The classifier (lives in open-api) is responsible for turning raw `os` /
 *     `manufacturer` / `model` / `osVersion` into a clean classification; this
 *     file only renders. Keeps labels trivially testable.
 *   - Pure strings. No React, no i18n. These are technical identifiers
 *     ("Fire OS", "Raspberry Pi", "ProPlayer Max") we deliberately do NOT
 *     translate — support + operators need a stable vocabulary across locales.
 *   - Deterministic fallbacks. A classification that hits `unknown` still
 *     renders something useful (raw vendor + model code) rather than an
 *     empty string.
 *
 * See docs/specs/19-optiedge-mdm/research/
 *     devices-compatibility-matrix-maintenance-best-practices.md §5.3, §5.6,
 *     §5.7 for the production profiling that drove this map (coverage went
 *     from ~78% → ~97% polished after these additions).
 */

import type { DeviceClassification } from './device-classification';

/**
 * Amazon Fire TV model-code → friendly label.
 *
 * Source: Amazon's device-code reference
 *   https://developer.amazon.com/docs/fire-tv/device-specifications-comparison-table.html
 *
 * Entries populated from BQ fleet profile (2026-04-23) — every code with ≥100
 * active devices gets an explicit label. Codes below that threshold fall
 * through to the generic "Fire TV Device" label; adding more is safe and
 * additive. Classifier detects the family via `vendor === 'amazon' &&
 * platform === 'android'`; this map resolves the *which* Fire TV.
 */
export const AMAZON_FIRE_TV_MODELS: Readonly<Record<string, string>> = {
  // Fire TV Sticks
  AFTMM: 'Fire TV Stick 4K Max',
  AFTKA: 'Fire TV Stick 4K Max (2nd Gen)',
  AFTKM: 'Fire TV Stick 4K Max (2024)',
  AFTSS: 'Fire TV Stick 4K',
  AFTKMST12: 'Fire TV Stick 4K (2023)',
  AFTDCT31: 'Fire TV Stick 4K (2024)',
  AFTDEC012E: 'Fire TV Stick 4K (2025)',
  AFTKRT: 'Fire TV Stick',
  AFTT: 'Fire TV Stick (Gen 2)',
  AFTSSS: 'Fire TV Stick Lite',
  AFTHY7ABBA: 'Fire TV Stick HD (2024)',
  AFTM: 'Fire TV Stick (Gen 1)',
  AFTN: 'Fire TV (Gen 3)',
  AFTR: 'Fire TV Cube',
  AFTS: 'Fire TV (Gen 2)',
  AFTB: 'Fire TV (Gen 1)',

  // Fire TV Omni Series (smart TVs)
  AFTTI43: 'Fire TV Omni 43"',
  AFTTIFF43: 'Fire TV Omni Series',
  AFTHA001: 'Fire TV Omni QLED',
  AFTHA002: 'Fire TV Omni QLED 55"',
  AFTHA003: 'Fire TV Omni QLED',
  AFTHA004: 'Fire TV Omni QLED 65"',
  AFTMD001: 'Fire TV 4-Series 43"',
  AFTMD002: 'Fire TV 4-Series 50"',
  AFTOLCBC82: 'Fire TV Omni QLED',
  AFTKA002: 'Fire TV Omni Mini-LED',
  AFTMA08C15: 'Fire TV Omni Mini-LED',

  // Fire TV Edition Smart TVs / Soundbar
  AFTALMO: 'Fire TV Soundbar',
  AFTEAMR311: 'Fire TV Edition Smart TV',
  AFTBAMR311: 'Fire TV Edition Smart TV',
  AFTJMST12: 'Fire TV Edition Smart TV',
};

/**
 * Vendor enum → display name. Kept close to labels because it's pure
 * presentation. The classifier emits kebab-case enum values; this renders
 * them. Entries absent here fall back to the raw `manufacturerRaw` string
 * off the classification, then to empty.
 */
const VENDOR_DISPLAY: Readonly<Record<string, string>> = {
  optisigns: 'OptiSigns',
  amazon: 'Amazon',
  google: 'Google',
  apple: 'Apple',
  samsung: 'Samsung',
  lg: 'LG',
  sony: 'Sony',
  tcl: 'TCL',
  hisense: 'Hisense',
  xiaomi: 'Xiaomi',
  roku: 'Roku',
  brightsign: 'BrightSign',
  rockchip: 'Rockchip',
  droidlogic: 'Droidlogic',
  amlogic: 'Amlogic',
  allwinner: 'Allwinner',
  tpv: 'TPV',
  sei: 'SEI',
};

/**
 * Prefer the enum-mapped display, fall back to the raw manufacturer string
 * from the device doc. Returns empty string if both are absent.
 */
function formatVendor(c: DeviceClassification): string {
  const mapped = VENDOR_DISPLAY[c.vendor];
  if (mapped) return mapped;
  return (c.manufacturerRaw ?? '').trim();
}

/**
 * One-line human OS label.
 *
 * Expects the classifier to have produced a clean `osVersionDisplay` string
 * already. The classifier is responsible for:
 *   - stripping firmware-ish noise:     "6.1.99-factory"       → "6"
 *   - stripping OS-name prefixes:       "windows-10"           → "10"
 *                                       "mac-os-x-15"          → "15"
 *   - extracting from embedded strings: "T-KSU2EC...Tizen 8.0" → "8.0"
 *   - blanking kernel versions:         OptiSigns Linux        → ""
 *   - normalizing case on OS itself:    "Apple TVOS" (data quirk) → platform='tvos'
 *
 * This formatter only decides which OS name to prepend.
 *
 * Examples:
 *   {platform:'android', vendor:'amazon', osVersionDisplay:'7.1.2'} → "Fire OS (Android 7.1.2)"
 *   {platform:'android', vendor:'droidlogic', osVersionDisplay:'9'} → "Android 9"
 *   {platform:'linux', hardwareFamily:'optisigns-pro-max', osVersionDisplay:''} → "Linux"
 *   {platform:'linux', osVersionDisplay:'6'}                        → "Linux 6"
 *   {platform:'roku', osVersionDisplay:'15.1'}                      → "Roku OS 15.1"
 *   {platform:'tvos', osVersionDisplay:'17.2'}                      → "tvOS 17.2"
 *   {platform:'brightsign', osVersionDisplay:'9'}                   → "BrightSign 9"
 *   {platform:'chrome-os', osVersionDisplay:''}                     → "Chrome OS"
 *   {platform:'unknown'}                                            → "—"
 */
export function formatOs(c: DeviceClassification): string {
  const version = (c.osVersionDisplay ?? '').trim();
  const withVersion = (base: string) => (version ? `${base} ${version}` : base);

  // Room-system devices (Zoom / Teams / Webex / Meet) — OS is irrelevant to
  // operators; surface the platform family directly. Early return before the
  // OS switch because `platform` on these rows is often 'unknown' (we learn
  // about them through the vendor cloud API, not a player heartbeat).
  switch (c.hardwareFamily) {
    case 'zoom-room':
      return 'Zoom Room';
    case 'teams-room':
      return 'Microsoft Teams Room';
    case 'teams-collab-bar':
      return 'Teams Collaboration Bar';
    case 'teams-surface-hub':
      return 'Microsoft Surface Hub';
    case 'webex-room':
      return 'Cisco Webex Room';
    case 'webex-board':
      return 'Cisco Webex Board';
    case 'webex-desk':
      return 'Cisco Webex Desk';
    case 'google-meet-room':
      return 'Google Meet Room';
  }

  switch (c.platform) {
    case 'android':
      // Amazon's Android fork gets the Fire OS prefix.
      return c.vendor === 'amazon'
        ? version
          ? `Fire OS (Android ${version})`
          : 'Fire OS'
        : withVersion('Android');

    case 'linux':
      return withVersion('Linux');
    case 'windows':
      return withVersion('Windows');
    case 'mac':
      return withVersion('macOS');
    case 'tizen':
      return withVersion('Tizen');
    case 'webos':
      return withVersion('webOS');
    case 'tvos':
      return withVersion('tvOS');
    case 'ios':
      return withVersion('iOS');
    case 'ipados':
      return withVersion('iPadOS');
    case 'roku':
      return withVersion('Roku OS');
    case 'brightsign':
      return withVersion('BrightSign');

    // Chrome OS devices in prod report osVersion='unknown' consistently —
    // suppress version entirely.
    case 'chrome-os':
      return 'Chrome OS';

    case 'unknown':
    default:
      return '—';
  }
}

/**
 * Friendly product/model label.
 *
 * Driven primarily by `hardwareFamily` (~30 prod-observed families). The raw
 * `model` code and `manufacturerRaw` string are used only for:
 *   - Fire TV code lookup (AMAZON_FIRE_TV_MODELS map)
 *   - OptiKiosk suffix ("OptiKiosk 101" from "optikiosk101")
 *   - Vendor-led smart TV / Android-box fallback labels
 *   - Unknown-family last-resort rendering
 *
 * Examples:
 *   {hardwareFamily:'optisigns-optistick-faraday'}             → "OptiStick (Faraday)"
 *   {hardwareFamily:'optisigns-pro-max'}                       → "ProPlayer Max"
 *   {hardwareFamily:'optisigns-pro-ubuntu'}                    → "ProPlayer Gen 2 (Ubuntu)"
 *   {hardwareFamily:'optisigns-optikiosk', modelCode:'optikiosk101'} → "OptiKiosk 101"
 *   {hardwareFamily:'other-android-firestick', modelCode:'AFTMM', vendor:'amazon'}
 *                                                              → "Fire TV Stick 4K Max"
 *   {hardwareFamily:'other-android-firestick', modelCode:'AFT9XYZ', vendor:'amazon'}
 *                                                              → "Fire TV Device"
 *   {hardwareFamily:'smart-tv-android', vendor:'tcl'}          → "TCL Smart TV"
 *   {hardwareFamily:'raspberry-pi'}                            → "Raspberry Pi"
 *   {hardwareFamily:'windows-pc'}                              → "Windows PC"
 *   {hardwareFamily:'apple-tv'}                                → "Apple TV"
 *   {hardwareFamily:'unknown', manufacturerRaw:'NVIDIA', modelCode:'Shield TV'}
 *                                                              → "NVIDIA Shield TV"
 *   {hardwareFamily:'unknown'}                                 → "—"
 */
export function formatModel(c: DeviceClassification): string {
  switch (c.hardwareFamily) {
    // ─── Third-party conference-room systems ─────────────────────────────
    // Cloud-managed hardware — the OptiSigns MDM surface reaches them via a
    // vendor API (Zoom Content API, MS Graph, Webex Cloud, Google Meet
    // hardware). For the device listing, vendor/model strings from the
    // cloud are noisy (Logitech Rally Bar / Poly Studio X70 / Neat Bar Pro
    // for Zoom Rooms); we want the higher-signal "Zoom Room" surface label
    // here so operators can tell the platform at a glance. The vendor-
    // specific hardware detail lands in Device Info / drawer.
    case 'zoom-room':
      return 'Zoom Room';
    case 'teams-room':
      return 'Microsoft Teams Room';
    case 'teams-collab-bar':
      return 'Teams Collaboration Bar';
    case 'teams-surface-hub':
      return 'Microsoft Surface Hub';
    case 'webex-room':
      return 'Cisco Webex Room';
    case 'webex-board':
      return 'Cisco Webex Board';
    case 'webex-desk':
      return 'Cisco Webex Desk';
    case 'google-meet-room':
      return 'Google Meet Room';

    // ─── OptiSigns Android hardware (OptiStick family) ───────────────────
    // Capability bucket is a single `optisigns-android` (spec 04 §12 decision).
    // SoC-specific variants (Faraday / SEI / Gen 4) differ only in display, so
    // `formatOptiStick` discriminates on `modelCode` here.
    case 'optisigns-android':
      return formatOptiStick(c);

    // ─── OptiSigns Pro (electron-based signage box) ──────────────────────
    case 'optisigns-pro-gen3':
      return 'OptiSigns Pro';
    case 'optisigns-pro-max':
      return 'OptiSigns ProMax';
    case 'optisigns-pro-gen2':
      return 'OptiSigns Pro Gen 2';
    case 'optisigns-pro-ubuntu':
      return 'OptiSigns Pro Gen 2 (Ubuntu)';
    case 'optisigns-pro-athena':
      return 'OptiSigns Pro Athena';

    // ─── OptiKiosk family ────────────────────────────────────────────────
    case 'optisigns-optikiosk':
      return formatOptiKiosk(c);

    // ─── Fire TV (Amazon Android fork) ───────────────────────────────────
    // Raw model code is the lookup key; unmapped codes get a generic label
    // rather than rendering raw (shipping a new Fire TV code doesn't break
    // the UI — we just show "Fire TV Device" until the map is updated).
    case 'other-android-firestick':
      if (c.modelCode && AMAZON_FIRE_TV_MODELS[c.modelCode]) {
        return AMAZON_FIRE_TV_MODELS[c.modelCode];
      }
      return 'Fire TV Device';

    // ─── Google streaming hardware ───────────────────────────────────────
    // Classifier groups these into one family; we disambiguate by the raw
    // model name which Google already ships user-friendly.
    case 'chromecast-google-tv': {
      const m = (c.modelCode ?? '').toLowerCase();
      if (m.includes('streamer')) return 'Google TV Streamer';
      if (m.includes('hd')) return 'Chromecast HD';
      return 'Chromecast';
    }

    // ─── Third-party smart TVs and Android boxes ─────────────────────────
    // Vendor-led labels. The specific model code (e.g. "BRAVIA 4K VH22") is
    // too noisy for an ops list; "Sony Smart TV" is what users mentally
    // group them as. Full model is still exposed in the Device Info panel.
    case 'smart-tv-android': {
      const vendor = formatVendor(c);
      return vendor ? `${vendor} Smart TV` : 'Android Smart TV';
    }
    case 'oem-android-box': {
      const vendor = formatVendor(c);
      return vendor ? `${vendor} Android Player` : 'Android Player';
    }

    // ─── Platform-native devices — model field is junk, label from OS ────
    // Windows reports model='x64', Mac reports 'x64', Linux on RPi reports
    // 'arm'/'arm64' — none of those are useful to surface.
    case 'windows-pc':
      return 'Windows PC';
    case 'macos':
      return 'Mac';
    case 'raspberry-pi':
      return 'Raspberry Pi';

    // ─── Apple consumer hardware ─────────────────────────────────────────
    case 'apple-tv':
      return 'Apple TV';
    case 'iphone':
      return 'iPhone';
    case 'ipad':
      return 'iPad';

    // ─── Other named platforms ──────────────────────────────────────────
    case 'roku':
      return c.modelCode ?? 'Roku Player';
    case 'brightsign':
      return c.modelCode ?? 'BrightSign Player';
    case 'chrome-os':
      return 'Chromebook';

    // ─── LG webOS variants ──────────────────────────────────────────────
    case 'webos-consumer':
      return 'LG webOS TV';
    case 'webos-signage':
      return 'LG webOS Signage';
    case 'webos-signage-athena':
      return 'LG webOS Signage (Athena)';

    // ─── Samsung display hardware ───────────────────────────────────────
    case 'samsung-sssp':
      return 'Samsung SSSP';
    case 'samsung-consumer-tv':
      return 'Samsung Smart TV';

    // ─── Third-party electron players (scio2's OTHER_ELECTRONS bucket) ──
    case 'other-electron':
      return 'Player (Electron)';

    // ─── Generic Android fallback (non-OptiSigns, non-Fire, no match) ───
    case 'other-android': {
      const vendor = formatVendor(c);
      return vendor ? `${vendor} Android` : 'Android Device';
    }

    // ─── Last resort — surface whatever we have so support can debug ────
    case 'unknown':
    default: {
      const vendor = formatVendor(c);
      const model = (c.modelCode ?? '').trim();
      if (vendor && model) return `${vendor} ${model}`;
      if (model) return model;
      if (vendor) return vendor;
      return '—';
    }
  }
}

/**
 * Render OptiKiosk variants. The model code's first two digits are the
 * screen size (inches); any trailing digits are a series / revision code
 * that isn't user-facing.
 *
 *   "optikiosk101"  → "OptiKiosk 10"   (10" display, series 1)
 *   "OptiKiosk321"  → "OptiKiosk 32"   (32" display, series 1)
 *
 * ShiMeta AIOT-3588 boards are white-labelled as the 50" SKU; they don't
 * carry an `optikiosk*` model string so they need a manufacturer-based
 * override.
 */
function formatOptiKiosk(c: DeviceClassification): string {
  const model = (c.modelCode ?? '').trim();
  const mfr = (c.manufacturerRaw ?? '').toLowerCase();
  if (mfr === 'shimeta' && /^aiot-3588/i.test(model)) return 'OptiKiosk 50';
  const match = model.match(/^opti_?kiosk[-_ ]?(\d{2})\d*$/i);
  if (match && match[1]) return `OptiKiosk ${match[1]}`;
  return 'OptiKiosk';
}

/**
 * Render OptiStick variants from the single `optisigns-android` capability
 * bucket. Capability treats Gen 2 / Gen 3 / Gen 4 identically; display
 * discriminates primarily on `platformRaw` (the board / SoC id), falling
 * back to `modelCode` + `manufacturerRaw` for legacy devices that never
 * populated `device.platform`.
 *
 *   platformRaw starts with 'SEI900'                        → "OptiStick Gen 4"
 *   platformRaw starts with 'SEI700'                        → "OptiStick Gen 3"
 *   platformRaw === 'faraday'                               → "OptiStick Gen 2"
 *   modelCode === 'optistick4'                              → "OptiStick Gen 4"
 *   modelCode === 'optisigns_player' + mfr 'Droidlogic'     → "OptiStick Gen 2"
 *   anything else                                           → "OptiStick"
 */
function formatOptiStick(c: DeviceClassification): string {
  const platform = (c.platformRaw ?? '').toLowerCase();
  const model = (c.modelCode ?? '').toLowerCase();
  const mfr = (c.manufacturerRaw ?? '').toLowerCase();

  if (platform.startsWith('sei900') || model === 'optistick4') return 'OptiStick Gen 4';
  if (platform.startsWith('sei700')) return 'OptiStick Gen 3';
  if (platform === 'faraday' || (model === 'optisigns_player' && mfr === 'droidlogic')) {
    return 'OptiStick Gen 2';
  }
  return 'OptiStick';
}
